import { memo, useEffect, useMemo, useRef, useState } from "react";
import { track } from "analytics/usageTracker";
import {
  FEATURE_ACTIVITY_EDITED,
  FEATURE_ADD_ACTIVITY,
  FEATURE_ADD_MULTIPLE_ACTIVITIES,
} from "analytics/featureCatalog";
import {
  Alert,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
  Radio,
  RadioGroup,
  FormControl,
  FormControlLabel,
  FormHelperText,
} from "@mui/material";

import { Close as CloseIcon } from "components/icons";
import { handleAxiosError } from "app/services/app.error.handler";
import {
  addActivityToBackend,
  addActivitiesToBackend,
  updateMemberSingleActivity,
  loadMembersFromBackendForClient,
} from "app/services/member.service";
// @since NEXT_VERSION
import { loadDayHoursForOwner } from "app/services/activity.day-hours.service";
import EntryRowsTable from "app/components/reports/bulk/EntryRowsTable";
import {
  makeRow,
  validateRows,
  toBulkPayload,
  dayRollUp,
  captureTypeNames,
  remapRowTypes,
  dropUnavailableTypes,
} from "utils/bulkActivityUtil";
import { loadMembersForManagerCached, loadScopeManagersForIds } from "app/services/manager.service";
import { ClientFilterSelect } from "app/components/common/ClientFilterSelect";
import * as AppConstants from "AppConstants";
import MDBox from "components/MDBox";
// @since NEXT_VERSION
import MDTypography from "components/MDTypography";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  getDateInFormat,
  getLatestActivityDate,
  getEarliestActivityDate,
  startOfLocalDay,
} from "utils/dateUtil";
import { toManagerClientOptions, toMemberClientOptions, toClientListOptions, mergeClientOptionsById } from "utils/clientOptions";
import { sortByName, sortByText } from "utils/optionSort";
import dayjs from "dayjs";
import { validateActivity, ACTIVITY_FIELD_MESSAGES } from "utils/activityValidationUtil";
import {
  readSessionCurrentClient,
  readSessionCurrentMember,
  getCurrentManager,
  readSessionCurrentManager,
  getMemberClients,
} from "app/services/app.state.service";
import { useDispatch, useSelector } from "react-redux";
import { loadAllActivityTypesByClient } from "app/services/activity.stats.service";
// @since NEXT_VERSION
import { loadActiveClientProjectsByClient } from "app/services/client-project.service";
import "./AddActivityDialog.css";
import { trimOnBlur, trimTextFields } from "utils/trimOnBlur";

// Who the activity is being logged for. Managers/admins default to MEMBER (the
// existing "log for a team member" flow); SELF logs the activity against the
// logged-in user's own member record.
const ACTIVITY_TARGET_MEMBER = "MEMBER";
const ACTIVITY_TARGET_SELF = "SELF";

// Prefix for manager-owned owner selections in the owner dropdown (on-behalf).
const MANAGER_OPTION_PREFIX = "MGR-";

// The name shown for an on-behalf manager option. Shared by the sort and the render so the
// two cannot drift: ordering on a different expression than the one displayed would put a
// record with no name on file (shown as "Manager 42") at the top of the group.
// @since NEXT_VERSION
const l1ManagerLabel = (mgr) => mgr.managerName ?? mgr.name ?? `Manager ${mgr.id}`;

// The name shown for a project option, and the key its list is ordered by. Shared for the
// same reason as l1ManagerLabel above: ordering on a different expression than the one
// displayed puts rows where the reader does not expect them. The "(inactive)" marker the
// render appends is deliberately not part of the key -- it flags state, not identity.
// @since NEXT_VERSION
const projectOptionLabel = (project) =>
  project?.code ? `${project.code} - ${project.name}` : project?.name;

// --- Pure helpers (exported for tests) ---

/**
 * Given the raw owner dropdown value (string), decode into the data patch for
 * isMember / ownerId / managerId.
 */
export const decodeOwnerValue = (value, parseNullableInt) => {
  if (typeof value === "string" && value.startsWith(MANAGER_OPTION_PREFIX)) {
    const mgrId = parseNullableInt(value.slice(MANAGER_OPTION_PREFIX.length));
    return { isMember: false, ownerId: null, managerId: mgrId };
  }
  return { isMember: true, ownerId: parseNullableInt(value), managerId: null };
};

/**
 * Returns true when the L2 manager directly manages the selected client (i.e. one of the
 * L2's own manager records for that client is in the subtree list). selfManagerIds accepts
 * a single id or a list — the L2 can hold one record per client, so all count as "self".
 */
export const computeManagesSelectedClient = (subtreeManagers, selfManagerIds, selectedClientId) => {
  const selfIds = new Set(
    (Array.isArray(selfManagerIds) ? selfManagerIds : [selfManagerIds]).filter((id) => id != null).map(String)
  );
  return subtreeManagers.some(
    (m) => selfIds.has(String(m.id)) && String(m.clientId) === String(selectedClientId)
  );
};

// Radios in an outlined box with a legend, so the caption sits on the border like any field
// label instead of taking a line of its own above the control. Border and radius come from
// the theme, so the box matches the notched outline of the fields beside it.
// @since NEXT_VERSION
const OutlinedRadioField = ({ label, children }) => (
  <MDBox
    component="fieldset"
    className="add-activity-radio-field"
    sx={{
      // MUI's own outline grey (light or dark), which is what the date field beside this one
      // draws. The theme's lighter borders.borderColor read as a weaker box next to it.
      border: ({ borders, palette }) =>
        `${borders.borderWidth[1]} solid ${
          palette.mode === "dark" ? "rgba(255, 255, 255, 0.23)" : "rgba(0, 0, 0, 0.23)"
        }`,
      borderRadius: ({ borders }) => borders.borderRadius.lg,
    }}
  >
    <MDBox component="legend" className="add-activity-radio-legend">
      {label}
    </MDBox>
    {children}
  </MDBox>
);

// The shared validator speaks of a start and an end date. The single-activity form shows one
// "Date" field and copies it to endDate, so its messages are reworded to name the field the user
// sees. @since NEXT_VERSION
const DATE_FIELD_MESSAGES = {
  [ACTIVITY_FIELD_MESSAGES.startDate.required]: "Pick a date.",
  [ACTIVITY_FIELD_MESSAGES.startDate.tooFarAhead]:
    `The date cannot be more than ${AppConstants.ACTIVITY_FUTURE_DAYS_ALLOWED} days ahead.`,
  [ACTIVITY_FIELD_MESSAGES.startDate.tooFarBack]:
    `The date cannot be more than ${AppConstants.ACTIVITY_PAST_MONTHS_ALLOWED} months old.`,
};

// @since NEXT_VERSION
const toDateFieldMessage = (message) => DATE_FIELD_MESSAGES[message] ?? message;

// Stable default for the cache props so an omitted prop doesn't create a new
// object each render (which would thrash the member/type effects).
const EMPTY_CACHE = {};

const obj = {
  startDate: getDateInFormat(new Date()),
  endDate: getDateInFormat(new Date()),
  hoursSpent: 1,
  ownerId: null,
  clientTicketRef: "",
  description: "",
  activityTypeId: null,
  // Optional: null means no project recorded, which is how every activity logged before
  // HRMS-259 reads. @since NEXT_VERSION
  projectId: null,
  status: AppConstants.DEFAULT_ACTIVITY_STATUS,
  location: "Offsite",
  isMember: true,
  managerId: null,
};

const AddActivityDialog = ({
  role,
  addActivityOpen,
  setAddActivityOpen,
  refreshActivitiesDataHandler,
  editObject,
  copyObject = null,
  // Which entry screen to show. The "Add Multiple Activities" button opens the table; Add, Edit
  // and Copy all open the single-activity form. Driven by the caller rather than inferred from
  // editObject, because Add now has two shapes. @since NEXT_VERSION
  bulkMode = false,
  // Per-client reference caches warmed on dashboard load. Read these before
  // hitting the network so changing the client dropdown doesn't re-fetch.
  membersByClient = EMPTY_CACHE,
  typesByClient = EMPTY_CACHE,
}) => {
  // The table is only ever the "add several" screen. One activity — new, edited or copied — is
  // entered on the single-activity form. @since NEXT_VERSION
  const isBulkMode = bulkMode;
  const [rows, setRows] = useState([]);
  const [rowErrors, setRowErrors] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [batchError, setBatchError] = useState(null);
  const [clientSwitchNotice, setClientSwitchNotice] = useState(null);
  // Saved hours, cached per owner per month. One request per month rather than one spanning the
  // whole batch: a month is at most 31 days, so the endpoint's 92-day range cap can never be
  // reached — with a single span, a row in January beside one in September was refused and every
  // badge silently vanished. Caching by month also makes paging the picker free.
  // @since NEXT_VERSION
  const [dayHours, setDayHours] = useState({ ownerKey: null, byMonth: {}, pendingMonths: [] });
  const [visibleMonth, setVisibleMonth] = useState(null);
  // Tracks the client the rows' activity types belong to, and whether a switch is waiting for the
  // new client's list to arrive before the types can be re-matched by name.
  const lastClientRef = useRef(null);
  const awaitingRemapRef = useRef(false);

  const [message, setMessage] = useState("");
  const [messageOpen, setMessageOpen] = useState(false);
  const [severity, setSeverity] = useState("info"); // "error" or "warning" or "info" or "success"
  const [data, setData] = useState(obj);
  const [fieldErrors, setFieldErrors] = useState({});
  const [activityTarget, setActivityTarget] = useState(ACTIVITY_TARGET_MEMBER);
  // Async member fetches resolve after the user may have switched to "Myself".
  // The member-load effect doesn't re-run on activityTarget changes, so its
  // closure holds a stale value; this ref always reflects the current target.
  const activityTargetRef = useRef(activityTarget);
  useEffect(() => {
    activityTargetRef.current = activityTarget;
  }, [activityTarget]);
  const [selfManagerId, setSelfManagerId] = useState(null);
  const [resolvingSelf, setResolvingSelf] = useState(false);
  // L2: all managers in this manager's subtree (self + L1 children); empty for L1s.
  const [subtreeManagers, setSubtreeManagers] = useState([]);
  // Self-target is only meaningful for managers — admins don't have a _manager row.
  // Enabled for both add and edit: in edit mode the radio is shown locked so a
  // manager's own activity reads as "Myself", and the self-profile + subtree
  // resolution it gates must run so the saved member / on-behalf manager can be
  // shown in the (disabled) dropdown.
  const selfTargetEnabled = role === AppConstants.ROLE_EMPLOYEE_MANAGER;
  const hoursInputRef = useRef(null);
  const [activityTypes, setActivityTypes] = useState([]);
  // Active projects for the selected client. @since NEXT_VERSION
  const [projects, setProjects] = useState([]);
  const locations = useSelector((state) => state.client.locations);
  const dispatch = useDispatch();
  // Session cache keyed by manager_id, read before hitting by-manager/{id}.
  const membersByManager = useSelector((state) => state.client?.membersByManager || EMPTY_CACHE);

  // Dialog-local client selection. The dialog now owns its client context
  // explicitly instead of deriving it from the (removed) global manager filter.
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [clientOptions, setClientOptions] = useState([]); // [{ id, label }]
  const [dialogMembers, setDialogMembers] = useState([]); // members for selectedClientId (admin/manager)
  const [memberClientsList, setMemberClientsList] = useState([]); // member-role memberships

  const parseNullableInt = (value) => {
    const parsed = parseInt(value);
    return Number.isNaN(parsed) ? null : parsed;
  };

  // Initialize the form once each time the dialog opens for a given target.
  // Guarded with a ref so later changes to async data (members / activity
  // types loading in) don't re-run this and wipe what the user has entered.
  const initializedRef = useRef(false);

  // On open, build the client dropdown options and pick the initial client for
  // the current role. Edit/copy start from the row's client; add starts from
  // the first available client.
  useEffect(() => {
    if (!addActivityOpen) return;
    const initialFromObject = editObject?.clientId ?? copyObject?.clientId ?? null;

    if (role === AppConstants.ROLE_EMPLOYEE_MEMBER) {
      getMemberClients().then((list) => {
        const memberships = Array.isArray(list) ? list : [];
        setMemberClientsList(memberships);
        setClientOptions(toMemberClientOptions(memberships));
        setSelectedClientId(String(initialFromObject ?? memberships[0]?.client?.id ?? ""));
      });
      return;
    }

    let optionList = [];
    if (role === AppConstants.ROLE_EMPLOYEE_MANAGER) {
      const list = readSessionCurrentManager();
      optionList = toManagerClientOptions(list);
    } else {
      const list = readSessionCurrentClient();
      optionList = toClientListOptions(list);
    }
    setClientOptions(optionList);
    setSelectedClientId(String(initialFromObject ?? optionList[0]?.id ?? ""));
  }, [addActivityOpen, role, editObject, copyObject]);

  // For the manager role: once the subtree loads, union its clients (incl. L1s') with the
  // manager's own session assignments so every client they can log against stays selectable.
  useEffect(() => {
    if (role !== AppConstants.ROLE_EMPLOYEE_MANAGER) return;
    if (subtreeManagers.length === 0) return;

    const subtreeClientOptions = Object.values(
      subtreeManagers.reduce((acc, m) => {
        if (m?.clientId != null && !acc[m.clientId]) {
          acc[m.clientId] = {
            id: String(m.clientId),
            label:
              m.clientCode && m.clientName
                ? `${m.clientCode} - ${m.clientName}`
                : m.clientName ?? m.clientCode ?? String(m.clientId),
          };
        }
        return acc;
      }, {})
    );

    const mergedClientOptions = mergeClientOptionsById(
      subtreeClientOptions,
      toManagerClientOptions(readSessionCurrentManager())
    );
    if (mergedClientOptions.length > 0) {
      setClientOptions(mergedClientOptions);
      // Keep the current selection if still valid; otherwise default to the first client.
      setSelectedClientId((prev) => {
        const stillValid = mergedClientOptions.some((o) => String(o.id) === String(prev));
        return stillValid ? prev : String(mergedClientOptions[0]?.id ?? prev);
      });
    }
  }, [subtreeManagers, role]);

  useEffect(() => {
    if (!addActivityOpen) {
      initializedRef.current = false;
      // Reset the target so a prior self-edit's SELF doesn't leak into the next open
      // (which would flash the Myself radio and clobber the restored member owner).
      setActivityTarget(ACTIVITY_TARGET_MEMBER);
      // @since NEXT_VERSION
      setRows([]);
      setRowErrors([]);
      setSubmitting(false);
      setBatchError(null);
      setClientSwitchNotice(null);
      lastClientRef.current = null;
      awaitingRemapRef.current = false;
      setDayHours({ ownerKey: null, byMonth: {}, pendingMonths: [] });
      setVisibleMonth(null);
      return;
    }
    if (initializedRef.current) return;
    initializedRef.current = true;

    setMessageOpen(false);
    setMessage("");
    setFieldErrors({});
    setActivityTarget(ACTIVITY_TARGET_MEMBER);

    if (editObject) {
      // Restore the owner type. A manager-owned row carries isMember === false with
      // the manager_id in memberId, so preserve it as managerId (not ownerId) —
      // otherwise the manager reads as a member whose id matches nothing and the
      // dropdown blanks. When the row is one of the logged-in manager's own records,
      // pre-select "Myself"; an L1 in the subtree stays "Team member" (MGR-<id>).
      const editIsMember = editObject.isMember !== false;
      const ownManagerIds = (readSessionCurrentManager() || []).map((r) => String(r?.id));
      const isOwnManagerRow = !editIsMember && ownManagerIds.includes(String(editObject.memberId));
      setActivityTarget(isOwnManagerRow ? ACTIVITY_TARGET_SELF : ACTIVITY_TARGET_MEMBER);
      setData({
        ...obj,
        isMember: editIsMember,
        ownerId: editIsMember ? parseNullableInt(editObject.memberId) : null,
        managerId: editIsMember ? null : parseNullableInt(editObject.memberId),
        startDate: editObject.startDate,
        endDate: editObject.endDate,
        hoursSpent: editObject.hoursSpent,
        clientTicketRef: editObject.clientTicketRef,
        description: editObject.description,
        status: editObject.status,
        activityTypeId: parseNullableInt(editObject.activityTypeId),
        projectId: parseNullableInt(editObject.projectId),
        location: editObject.location ? editObject.location : "Offsite",
      });
      return;
    }

    if (copyObject) {
      // Copy mode: carry every field over from the source activity, but start the
      // duplicate fresh — empty dates, zero hours, default status. Preserve the
      // owner type: a manager's own self-activity (isMember === false) is copied
      // as a self-activity (managerId is re-resolved below for the current
      // manager); everything else copies as a member activity.
      const copyAsSelf = copyObject.isMember === false && selfTargetEnabled;
      if (copyAsSelf) setActivityTarget(ACTIVITY_TARGET_SELF);
      setData({
        ...obj,
        isMember: !copyAsSelf,
        ownerId: copyAsSelf ? null : parseNullableInt(copyObject.memberId),
        managerId: null,
        startDate: null,
        endDate: null,
        hoursSpent: 0,
        clientTicketRef: copyObject.clientTicketRef,
        description: copyObject.description,
        status: AppConstants.DEFAULT_ACTIVITY_STATUS,
        activityTypeId: parseNullableInt(copyObject.activityTypeId),
        projectId: parseNullableInt(copyObject.projectId),
        location: copyObject.location ? copyObject.location : "Offsite",
      });
      return;
    }

    // The table opens with one row dated today; the single form ignores it. @since NEXT_VERSION
    setRows([makeRow({ date: getDateInFormat(new Date()) })]);

    let memberId = null;
    if (role === AppConstants.ROLE_EMPLOYEE_MEMBER) {
      const member = readSessionCurrentMember();
      memberId = parseNullableInt(member?.id);
    } else if (dialogMembers.length > 0) {
      memberId = parseNullableInt(dialogMembers[0]?.id);
    }

    setData({
      ...obj,
      ownerId: memberId,
      activityTypeId: activityTypes.length > 0 ? parseNullableInt(activityTypes[0]?.id) : null,
    });
  }, [addActivityOpen, role, dialogMembers, activityTypes, editObject, copyObject]);

  // Reconcile async-loaded data with the current form without clobbering input:
  //   - Add mode: fill the member / activity-type defaults once the lists arrive.
  //   - Copy mode: drop a carried-over activity type the client no longer offers
  //     (e.g. it was deactivated since the original activity was logged), so the
  //     user is forced to pick a valid one instead of silently saving a stale id.
  useEffect(() => {
    if (!addActivityOpen) return;

    setData((prev) => {
      let next = prev;

      if (!editObject && !copyObject) {
        if (
          next.ownerId == null &&
          next.isMember !== false &&
          role !== AppConstants.ROLE_EMPLOYEE_MEMBER &&
          dialogMembers.length > 0
        ) {
          next = { ...next, ownerId: parseNullableInt(dialogMembers[0]?.id) };
        }
        if (next.activityTypeId == null && activityTypes.length > 0) {
          next = { ...next, activityTypeId: parseNullableInt(activityTypes[0]?.id) };
        }
      }

      if (copyObject && next.activityTypeId != null && activityTypes.length > 0) {
        const offered = activityTypes.some(
          (type) => parseNullableInt(type?.id) === next.activityTypeId
        );
        if (!offered) next = { ...next, activityTypeId: null };
      }

      // Same guard for the project: the source activity's project may have been deactivated since
      // it was logged, in which case the dropdown no longer offers it and a stale id would be
      // rejected by the backend on save. @since NEXT_VERSION
      if (copyObject && next.projectId != null && projects.length > 0) {
        const offered = projects.some(
          (project) => parseNullableInt(project?.id) === next.projectId
        );
        if (!offered) next = { ...next, projectId: null };
      }

      return next;
    });
  }, [addActivityOpen, role, dialogMembers, activityTypes, editObject, copyObject]);

  // Refresh the Activity Type list whenever the chosen client changes. Prefer the
  // cache warmed on dashboard load; only fetch when this client isn't cached yet.
  useEffect(() => {
    if (!selectedClientId) return;

    const applyTypes = (types) => {
      setActivityTypes(types);
      // Add mode: default (and reset on client change) to the new client's first
      // type, so the dropdown is never blank and a stale (other-client) type is
      // never submitted. Edit/copy keep their carried-over type.
      if (!editObject && !copyObject) {
        setData((prev) => ({ ...prev, activityTypeId: parseNullableInt(types[0]?.id) }));
      }
    };

    const cached = typesByClient[String(selectedClientId)];
    if (cached) {
      applyTypes(cached);
      return;
    }

    loadAllActivityTypesByClient(selectedClientId)
      .then((response) => applyTypes(response.data || []))
      .catch((error) => {
        setActivityTypes([]);
        handleAxiosError(error);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClientId, editObject, copyObject, typesByClient]);

  // Refresh the Project list whenever the chosen client changes, mirroring the Activity Type
  // effect above, with one deliberate difference: projects are NOT read from a warmed cache.
  //
  // Activity types are near-static, so caching them costs nothing. Projects are created and
  // deactivated continuously — that is the point of this feature — and the warmed cache was only
  // repopulated on a full dashboard load. A project deactivated mid-session stayed selectable
  // until the backend refused the save, and one reactivated mid-session stayed missing with no
  // explanation at all. Fetching per open is one small per-client request and keeps the dropdown
  // honest.
  //
  // Unlike Activity Type, the project is optional, so there is no default-to-first behaviour;
  // a fresh add starts on "None". Changing client in add mode still clears the selection, since
  // a project from the previous client would be rejected by the backend's cross-client check.
  // @since NEXT_VERSION
  useEffect(() => {
    // Keyed on addActivityOpen as well as the client: reopening the dialog does not change
    // selectedClientId, so without it the list would still be fetched only once per session and
    // fetching per open would buy nothing.
    if (!addActivityOpen || !selectedClientId) return;

    const applyProjects = (list) => {
      const active = Array.isArray(list) ? list : [];

      // A project deactivated since the activity was logged is absent from the active list, so the
      // Select would match no option and paint the field blank — the project would read as
      // cleared when it is not, and indistinguishable from the user having chosen None. Append it
      // flagged, built from the code and name the report row already carries, so no extra fetch is
      // needed. Add mode is unaffected: a deactivated project must not be offered for new work.
      // @since NEXT_VERSION
      const editedProjectId = parseNullableInt(editObject?.projectId);
      const inactiveAndMissing =
        editedProjectId != null &&
        !active.some((project) => parseNullableInt(project?.id) === editedProjectId);

      // Ordered by the label the dropdown shows, so the list reads alphabetically by project
      // code. This overrides the displayOrder that findActiveByClient sorts by, which no other
      // screen depends on: the Project master grid reads GET /projects, a bare findAll() with
      // no ordering at all, so nothing was agreeing with displayOrder to begin with.
      setProjects(
        sortByText(
          inactiveAndMissing
            ? [
                ...active,
                {
                  id: editedProjectId,
                  code: editObject.projectCode ?? null,
                  name: editObject.projectName ?? `Project ${editedProjectId}`,
                  inactive: true,
                },
              ]
            : active,
          projectOptionLabel
        )
      );

      if (!editObject && !copyObject) {
        setData((prev) => (prev.projectId == null ? prev : { ...prev, projectId: null }));
      }
    };

    loadActiveClientProjectsByClient(selectedClientId)
      .then((response) => applyProjects(response.data || []))
      .catch((error) => {
        setProjects([]);
        handleAxiosError(error);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addActivityOpen, selectedClientId, editObject, copyObject]);

  // Admin/manager: the client dropdown filters the member dropdown. Load the
  // selected client's members for the dropdown. A manager sees only the members
  // reporting to the manager record for the selected client (which may be an L1
  // in the subtree for an L2 user); an admin sees every member of the client. In
  // a fresh add we also default the owner to the first member; in edit/copy the
  // owner is already carried over from the source row, so we only refresh the list.
  useEffect(() => {
    if (role === AppConstants.ROLE_EMPLOYEE_MEMBER) return;
    // Editing shows a single locked option (the row's memberName), so the member
    // list is never read — skip the fetch. (The client field's label comes from the
    // session/subtree client options, not from this list.)
    if (editObject) return;
    if (!selectedClientId) return;

    const applyMembers = (unsorted) => {
      // Sorted here rather than at render, so the default owner picked below is the first
      // name the user sees rather than the client's oldest member record.
      const list = sortByName(unsorted);
      setDialogMembers(list);
      // On a fresh add, switching client resets the owner to that client's first member.
      // Also clear any on-behalf manager selection (isMember=false/managerId) carried over
      // from the previous client so a stale L1 can't be submitted against the new client.
      // "Myself" is left untouched here — the self-fallback effect re-evaluates it per client.
      if (!editObject && !copyObject && activityTargetRef.current !== ACTIVITY_TARGET_SELF) {
        setData((prev) => ({
          ...prev,
          isMember: true,
          managerId: null,
          ownerId: parseNullableInt(list[0]?.id),
        }));
      }
    };

    // The per-client cache holds one manager's members; for a manager it can't represent
    // a client with several subtree managers (the L2 + its L1s), so use it only for others.
    const cached =
      role !== AppConstants.ROLE_EMPLOYEE_MANAGER ? membersByClient[String(selectedClientId)] : undefined;
    if (cached) {
      applyMembers(cached);
      return;
    }

    if (role === AppConstants.ROLE_EMPLOYEE_MANAGER) {
      // A selected client can have several subtree managers (the L2 and/or its L1s).
      // Aggregate every such manager's members so the L1s' members show too. Fall back to
      // the L2's own record while the subtree hasn't loaded.
      const managersForClient = subtreeManagers.filter(
        (m) => String(m.clientId) === String(selectedClientId)
      );
      const ids =
        managersForClient.length > 0
          ? managersForClient.map((m) => parseNullableInt(m.id))
          : [managerIdForSelectedClient()].filter((id) => id != null);
      if (ids.length === 0) {
        applyMembers([]);
        return;
      }
      // Fetch each subtree manager's members through the cache, then aggregate and de-dupe.
      Promise.all(
        ids.map((id) => loadMembersForManagerCached(id, membersByManager, dispatch))
      ).then((lists) => {
        const byId = new Map(lists.flat().map((m) => [m.id, m]));
        applyMembers(Array.from(byId.values()));
      });
      return;
    }

    loadMembersFromBackendForClient(selectedClientId)
      .then((r) => applyMembers(r.status === 204 ? [] : r.data))
      .catch(() => setDialogMembers([]));
    // membersByManager is read but omitted from deps: the dialog writes to it, so
    // depending on it would self-trigger a re-run (client/subtree changes suffice).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClientId, role, editObject, copyObject, membersByClient, subtreeManagers]);

  // Member role: logging for themselves — set the owner to the member id of the
  // membership matching the chosen client.
  useEffect(() => {
    if (role !== AppConstants.ROLE_EMPLOYEE_MEMBER) return;
    if (!selectedClientId) return;
    const membership = memberClientsList.find(
      (m) => String(m.client.id) === String(selectedClientId)
    );
    if (membership) setData((prev) => ({ ...prev, ownerId: parseNullableInt(membership.id) }));
  }, [selectedClientId, role, memberClientsList]);

  // Resolve the logged-in manager's manager_id so they can log a self-activity.
  // Backend treats activities with isMember=false as manager-owned; ownerId is null
  // and managerId is the actor. If resolution fails, the "Myself" option stays disabled.
  useEffect(() => {
    if (!addActivityOpen || !selfTargetEnabled) return;

    let cancelled = false;
    setResolvingSelf(true);
    getCurrentManager()
      .then((manager) => {
        if (!cancelled) setSelfManagerId(parseNullableInt(manager?.id));
      })
      .catch(() => {
        if (!cancelled) setSelfManagerId(null);
      })
      .finally(() => {
        if (!cancelled) setResolvingSelf(false);
      });

    return () => {
      cancelled = true;
    };
  }, [addActivityOpen, selfTargetEnabled]);

  // The logged-in person's own manager record ids (one per client), plus the resolved
  // record. "Self" is this whole set, so a client managed through any of the L2's records
  // counts as directly managed and an L1 under any record stays in scope.
  const selfManagerIds = useMemo(() => {
    const ids = (readSessionCurrentManager() || []).map((c) => c?.id).filter((id) => id != null);
    return selfManagerId != null && !ids.includes(selfManagerId) ? [...ids, selfManagerId] : ids;
  }, [selfManagerId]);

  // Load the subtree of managers (self + L1 children) for the L2 hierarchy flow, rolled
  // up across ALL the manager's records — not just the resolved first one — so an L1
  // reporting through any of the L2's records is included. An L1 gets just themselves.
  useEffect(() => {
    if (!addActivityOpen || !selfTargetEnabled || !selfManagerId) {
      setSubtreeManagers([]);
      return;
    }
    loadScopeManagersForIds(selfManagerIds)
      .then((list) => setSubtreeManagers(list))
      .catch(() => setSubtreeManagers([]));
  }, [addActivityOpen, selfTargetEnabled, selfManagerId, selfManagerIds]);

  // For a self-activity, the actor is the manager record tied to the selected
  // client (a manager may manage several). Falls back to the resolved
  // selfManagerId if no record matches (e.g. before options have loaded).
  const managerIdForSelectedClient = () => {
    const list = readSessionCurrentManager();
    const rec = (Array.isArray(list) ? list : []).find(
      (c) => String(c.clientId) === String(selectedClientId)
    );
    return rec ? parseNullableInt(rec.id) : selfManagerId;
  };

  // True when one of the L2's own records covers the selected client. Gates the "Myself"
  // radio so the user can't log a self-activity against a client they don't directly manage.
  const managesSelectedClient = computeManagesSelectedClient(subtreeManagers, selfManagerIds, selectedClientId);

  // "Myself" is enabled only for managers (selfTargetEnabled) who have resolved
  // their manager profile (selfManagerId) AND the selected client is one they
  // directly manage.
  const selfRadioEnabled = selfTargetEnabled && Boolean(selfManagerId) && managesSelectedClient;

  const handleActivityTargetChange = (target) => {
    setActivityTarget(target);
    if (target === ACTIVITY_TARGET_SELF) {
      setData((prev) => ({
        ...prev,
        isMember: false,
        managerId: managerIdForSelectedClient(),
        ownerId: null,
      }));
      setFieldErrors((prev) => ({ ...prev, ownerId: undefined, managerId: undefined }));
    } else {
      setData((prev) => ({
        ...prev,
        isMember: true,
        managerId: null,
        ownerId: parseNullableInt(dialogMembers[0]?.id),
      }));
      setFieldErrors((prev) => ({ ...prev, managerId: undefined }));
    }
  };

  // Owner dropdown handler for the manager role: decodes member IDs and the
  // MGR-<id> sentinel for on-behalf L1 manager activities.
  const handleOwnerSelect = (value) => {
    const decoded = decodeOwnerValue(value, parseNullableInt);
    setActivityTarget(ACTIVITY_TARGET_MEMBER); // dropdown drives owner, not Self
    setData((prev) => ({ ...prev, ...decoded }));
  };

  // Keep the resolved manager id on the form while targeting "Myself". Covers
  // both the radio selection and a copied self-activity that defaults to SELF
  // before getCurrentManager has resolved.
  useEffect(() => {
    if (editObject) return; // edit: owner is locked and fully set by init; never re-sync
    if (!addActivityOpen || activityTarget !== ACTIVITY_TARGET_SELF) return;
    const managerId = managerIdForSelectedClient();
    setData((prev) =>
      prev.isMember === false && prev.managerId === managerId
        ? prev
        : { ...prev, isMember: false, ownerId: null, managerId }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addActivityOpen, activityTarget, selfManagerId, selectedClientId]);

  // If the user switches to a client they don't directly manage while "Myself"
  // is selected, fall back to "Team member" so the form stays valid.
  // Only fires once the subtree has loaded (subtreeManagers non-empty) to avoid
  // a false-positive fallback on initial mount before the async fetch completes.
  useEffect(() => {
    if (editObject) return; // edit: owner is locked, never auto-flip the target
    if (subtreeManagers.length === 0) return; // subtree not yet loaded; don't act yet
    if (activityTarget === ACTIVITY_TARGET_SELF && !selfRadioEnabled) {
      handleActivityTargetChange(ACTIVITY_TARGET_MEMBER);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClientId, selfRadioEnabled, subtreeManagers]);

  // Drops one field's inline error as soon as the user changes it, so a message from the
  // last save attempt does not sit under a field that is now valid. @since NEXT_VERSION
  const clearFieldError = (field) =>
    setFieldErrors((prev) =>
      prev[field] === undefined ? prev : { ...prev, [field]: undefined }
    );

  /**
   * Saves the one activity the form holds, new or edited.
   *
   * Problems are reported against their own field, so the Alert at the foot is left for server
   * failures. endDate has no field of its own: it only ever mirrors the Date error, which is
   * already shown.
   *
   * @since NEXT_VERSION
   */
  const saveActivityInBackend = async () => {
    setMessage("");
    setMessageOpen(false);

    const payload = trimTextFields(data);
    const { isValid, fieldErrors: errs } = validateActivity(payload, {
      earliestDate: earliestDateAllowed,
    });
    if (!isValid) {
      setFieldErrors(errs);
      if (errs.hoursSpent && hoursInputRef.current) {
        hoursInputRef.current.focus();
      }
      return;
    }

    setFieldErrors({});
    if (editObject) await updateActivityInvoker(payload);
    else await addActivityInvoker(payload);
  };

  /**
   * Validates every row, then saves the batch in one request.
   *
   * Validation runs client-side first so the common mistakes — a row with no type, a day over 24 h
   * — are shown against the row rather than coming back as a 422 naming only the first problem.
   *
   * @since NEXT_VERSION
   */
  // A client switch invalidates every row's activityTypeId — types are per client. Rather than
  // discard the selections, each row's type NAME is captured here while the OLD list is still
  // loaded, and re-matched against the new client's list in the effect below, so "Development"
  // survives a switch even though its id changes. The project is always cleared: project codes are
  // client-specific, and silently attaching a same-named project from another client is worse.
  // @since NEXT_VERSION
  useEffect(() => {
    if (!addActivityOpen || !isBulkMode) return;
    // The first pass records the opening client without touching anything — a copied row's type
    // must survive the initial render.
    if (lastClientRef.current === null) {
      lastClientRef.current = selectedClientId;
      return;
    }
    if (lastClientRef.current === selectedClientId) return;
    lastClientRef.current = selectedClientId;

    setRows((previous) => captureTypeNames(previous, activityTypes));
    setData((previous) => ({ ...previous, projectId: null }));
    setRowErrors([]);
    setBatchError(null);
    setClientSwitchNotice(null);
    awaitingRemapRef.current = true;
    // activityTypes is read, not depended on: this must fire on the client change alone, while the
    // OLD list is still in state. Depending on it would re-run after the new list lands and capture
    // the wrong names.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClientId, addActivityOpen, isBulkMode]);

  // Reconciles the rows against whatever type list is now loaded. After a client switch that means
  // re-matching by name; otherwise it drops a type the client does not offer, which is how a copied
  // activity's retired type is cleared rather than posted back.
  // @since NEXT_VERSION
  useEffect(() => {
    if (!addActivityOpen || !isBulkMode) return;

    // A client switch is reconciled against whatever the new client offers, including nothing at
    // all. Waiting for a non-empty list here left the flag set and the previous client's type ids
    // on the rows, ready to be posted, whenever the new client had no types or its list failed to
    // load -- which is exactly when those ids are least valid. @since NEXT_VERSION
    if (awaitingRemapRef.current) {
      awaitingRemapRef.current = false;
      const result = remapRowTypes(rows, activityTypes ?? []);
      setRows(result.rows);
      if (result.cleared > 0) {
        setClientSwitchNotice(
          `Activity type cleared on row ${result.clearedRowNumbers.join(", ")} — the selected ` +
            `client has no matching type. Project was cleared too.`
        );
      }
      return;
    }

    if (!activityTypes || activityTypes.length === 0) return;
    setRows((previous) => dropUnavailableTypes(previous, activityTypes));
    // rows is read for the remap but deliberately not a dependency: this must fire on the type
    // list arriving, not on every keystroke in a row.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activityTypes, addActivityOpen, isBulkMode]);

  const ownerKey = data.isMember === false ? `MGR-${data.managerId}` : `MBR-${data.ownerId}`;
  const dayHoursOwnerId = data.isMember === false ? data.managerId : data.ownerId;

  // Months the rows touch, plus the one the picker is showing.
  // @since NEXT_VERSION
  const neededMonths = useMemo(() => {
    const months = new Set();
    rows.forEach((row) => {
      if (row.date) months.add(dayjs(row.date).format("YYYY-MM"));
    });
    if (visibleMonth) months.add(visibleMonth);
    if (months.size === 0) months.add(dayjs().format("YYYY-MM"));
    return Array.from(months).sort();
  }, [rows, visibleMonth]);

  useEffect(() => {
    if (!addActivityOpen || !isBulkMode || dayHoursOwnerId == null) return;

    const ownerChanged = dayHours.ownerKey !== ownerKey;
    const missing = neededMonths.filter(
      (month) =>
        ownerChanged ||
        (dayHours.byMonth[month] === undefined && !dayHours.pendingMonths.includes(month))
    );
    if (missing.length === 0) return;

    // The owner's figures are dropped the instant the owner changes, so the previous person's
    // hours are never shown against this one's rows while the new read is in flight.
    setDayHours((previous) =>
      previous.ownerKey === ownerKey
        ? { ...previous, pendingMonths: [...previous.pendingMonths, ...missing] }
        : { ownerKey, byMonth: {}, pendingMonths: missing }
    );

    missing.forEach((month) => {
      const from = dayjs(`${month}-01`).startOf("month").format("YYYY-MM-DD");
      const to = dayjs(`${month}-01`).endOf("month").format("YYYY-MM-DD");
      loadDayHoursForOwner(dayHoursOwnerId, data.isMember !== false, from, to).then((byDate) => {
        setDayHours((previous) => {
          // A response for an owner that is no longer selected is dropped: it would otherwise
          // paint one person's hours onto another's rows.
          if (previous.ownerKey !== ownerKey) return previous;
          return {
            ownerKey,
            byMonth: { ...previous.byMonth, [month]: byDate },
            pendingMonths: previous.pendingMonths.filter((pending) => pending !== month),
          };
        });
      });
    });
    // dayHours is read for the cache check but deliberately not a dependency: depending on it
    // would re-run this effect on every resolved fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addActivityOpen, isBulkMode, ownerKey, dayHoursOwnerId, neededMonths]);

  // The flat { date: hours } view the table and the validator read, restricted to this owner.
  // @since NEXT_VERSION
  const savedHoursByDate = useMemo(() => {
    if (dayHours.ownerKey !== ownerKey) return {};
    return neededMonths.reduce(
      (accumulator, month) => Object.assign(accumulator, dayHours.byMonth[month] ?? {}),
      {}
    );
  }, [dayHours, ownerKey, neededMonths]);

  const savedHoursLoading =
    isBulkMode && dayHoursOwnerId != null &&
    (dayHours.ownerKey !== ownerKey || dayHours.pendingMonths.length > 0);

  const saveActivitiesInBackend = async () => {
    setMessage("");
    setMessageOpen(false);
    setBatchError(null);

    // Owner and rows are judged together and reported together: returning early on the owner would
    // hide every row problem behind it, so the user would fix one thing and be told about the next.
    const ownerValid = data.isMember === false ? data.managerId != null : data.ownerId != null;
    const ownerErrors = ownerValid
      ? {}
      : data.isMember === false
      ? { managerId: ACTIVITY_FIELD_MESSAGES.managerId.required }
      : { ownerId: ACTIVITY_FIELD_MESSAGES.ownerId.required };

    // The 24-hour check is only as good as the figures behind it. While they are loading
    // savedHoursByDate is empty, so a day that really holds 20 h would look free and the user would
    // be sent to a server 422 — the exact failure client-side validation exists to prevent.
    if (savedHoursLoading) {
      setBatchError("Checking what is already logged for this person — one moment.");
      return;
    }

    const result = validateRows(rows, savedHoursByDate);

    setFieldErrors(ownerErrors);
    if (!ownerValid || !result.isValid) {
      setRowErrors(result.rowErrors);
      setBatchError(result.batchError);
      return;
    }

    setRowErrors([]);
    setSubmitting(true);
    try {
      const payload = toBulkPayload({
        ownerId: data.ownerId,
        isMember: data.isMember !== false,
        managerId: data.managerId,
        projectId: data.projectId,
        location: data.location,
        rows,
      });
      await addActivitiesToBackend(payload);
      track(FEATURE_ADD_MULTIPLE_ACTIVITIES);
      setSuccessAction(
        rows.length === 1 ? "Activity added successfully" : `${rows.length} activities added successfully`
      );
      setTimeout(() => {
        setData(obj);
        setRows([]);
        setAddActivityOpen(false);
        setMessage("");
        setMessageOpen(false);
        refreshActivitiesDataHandler();
      }, 1000);
    } catch (error) {
      handleAxiosError(error);
      setErrorAction(extractServerMessage(error, "Failed to add the entries. Please try again."));
      // Only a failure puts Save back. On success the dialog stays up for a second to show the
      // message, and re-enabling the button for that second is long enough to write the whole
      // batch twice. Closing resets this. @since NEXT_VERSION
      setSubmitting(false);
    }
  };

  const addActivityInvoker = async (activityObject) => {
    await addActivityToBackend(activityObject)
      .then(() => {
        track(FEATURE_ADD_ACTIVITY);
        setSuccessAction("Activity added successfully");
        setTimeout(() => {
          setData(obj);
          setAddActivityOpen(false);
          setMessage("");
          setMessageOpen(false);
          refreshActivitiesDataHandler();
        }, 1000);
      })
      .catch((error) => {
        handleAxiosError(error);
        setErrorAction(extractServerMessage(error, "Failed to add activity. Please try again."));
      });
  };

  const updateActivityInvoker = async (data) => {
    if (!editObject?.id) {
      setErrorAction("Unable to update activity. Missing activity identifier.");
      return;
    }

    const idArray = editObject.id.split("-");
    const editObjectId = parseNullableInt(idArray[1] ?? editObject.id);
    if (!editObjectId) {
      setErrorAction("Unable to update activity. Invalid activity identifier.");
      return;
    }

    await updateMemberSingleActivity(editObjectId, data)
      .then((data) => {
        track(FEATURE_ACTIVITY_EDITED);
        setSuccessAction("Activity Updated successfully");
        setTimeout(() => {
          setData(obj);
          setAddActivityOpen(false);
          setMessage("");
          setMessageOpen(false);
          refreshActivitiesDataHandler();
        }, 1000);
      })
      .catch((error) => {
        handleAxiosError(error);
        setErrorAction(extractServerMessage(error, "Failed to update activity. Please try again."));
      });
  };

  const extractServerMessage = (error, fallback) => {
    const data = error?.response?.data;
    if (typeof data === "string" && data.trim()) return data;
    if (data && typeof data.message === "string" && data.message.trim()) return data.message;
    return fallback;
  };

  const setErrorAction = (errorMessage) => {
    setMessage(errorMessage);
    setMessageOpen(true);
    setSeverity("error");
  };

  const setSuccessAction = (message) => {
    setMessage(message);
    setMessageOpen(true);
    setSeverity("success");
  };

  // The oldest day this dialog will take. Editing relaxes it to the date the activity already
  // carries: the backdating window exists to stop new work being logged against a closed period,
  // not to make an old record impossible to correct. @since NEXT_VERSION
  const earliestDateAllowed = useMemo(() => {
    const standard = getEarliestActivityDate();
    const saved = editObject?.startDate ? startOfLocalDay(editObject.startDate) : null;
    return saved && saved < standard ? saved : standard;
  }, [editObject]);

  // One Location control, placed differently per mode: with Client and Project for a batch (it
  // applies to every entry in it), and beside Date when editing a single activity.
  // @since NEXT_VERSION
  const locationField = (
    <MDBox>
      <OutlinedRadioField label="Location">
        <RadioGroup
          row
          className="add-activity-radio-row"
          aria-label="Location"
          defaultValue={locations[1]}
          name="radio-buttons-group"
          value={data && data.location}
          onChange={(e) => setData({ ...data, location: e.target.value })}
        >
          {locations &&
            locations.map((option, index) => (
              <FormControlLabel value={option} key={index} control={<Radio />} label={option} />
            ))}
        </RadioGroup>
      </OutlinedRadioField>
    </MDBox>
  );

  // The owner dropdown's current value: MGR-<id> for an on-behalf manager, else the member id.
  const ownerFieldValue =
    role === AppConstants.ROLE_EMPLOYEE_MANAGER && !data.isMember && data.managerId
      ? `${MANAGER_OPTION_PREFIX}${data.managerId}`
      : (data.ownerId ?? "");

  return (
    // The table needs room for its seven columns; one activity keeps the narrow dialog it has
    // always had. Description is the table's only column that flexes, so 1138px is what leaves it
    // 246px. @since NEXT_VERSION
    <Dialog
      open={addActivityOpen}
      fullWidth={!isBulkMode}
      maxWidth={isBulkMode ? false : "sm"}
      PaperProps={{
        className: "add-activity-dialog",
        ...(isBulkMode ? { sx: { width: "min(1138px, 95vw)" } } : {}),
      }}
    >
      <DialogTitle>
        {isBulkMode
          ? `Add Multiple Activities`
          : editObject
          ? `Update Activity`
          : copyObject
          ? `Copy Activity`
          : `Add New Activity`}
      </DialogTitle>
      <DialogContent sx={{ p: 4, pt: 2 }}>
        {/* Client, Project and (for a batch) Location are the choices that hold for the whole
            dialog rather than for one entry, so they share the top line. Project is optional, so
            "None" is always offered and is the default on a fresh add; only the selected client's
            active projects are listed, and a deactivated project stays out of the list but remains
            visible on activities already logged against it. @since NEXT_VERSION */}
        <MDBox
          my={2}
          display="grid"
          gap={2}
          alignItems="flex-start"
          sx={{
            gridTemplateColumns: {
              xs: "1fr",
              sm: isBulkMode ? "repeat(3, minmax(0, 1fr))" : "repeat(2, minmax(0, 1fr))",
            },
          }}
        >
          {clientOptions.length > 0 &&
            (editObject ? (
              // Editing an existing activity: its client is fixed (an activity is
              // never reassigned to another client), so show a disabled field.
              <MDBox>
                <TextField
                  select
                  fullWidth
                  disabled
                  variant="outlined"
                  id="add-activity-client"
                  label="Client"
                  value={selectedClientId ?? ""}
                  sx={{ width: "100%" }}
                >
                  {clientOptions.filter(Boolean).map((opt) => (
                    <MenuItem key={opt.id} value={opt.id}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>
              </MDBox>
            ) : (
              <MDBox>
                <ClientFilterSelect
                  value={selectedClientId}
                  onChange={setSelectedClientId}
                  options={clientOptions}
                  includeAll={false}
                  label="Client"
                  id="add-activity-client"
                  sx={{ width: "100%" }}
                />
              </MDBox>
            ))}
          <MDBox>
            <TextField
              select
              fullWidth
              variant="outlined"
              id="add-activity-project"
              label="Project"
              value={data?.projectId ?? ""}
              // displayEmpty renders the "None" item as the closed field's text. Without it MUI
              // paints a zero-width space for an empty value, so an activity with no project would
              // read as a blank field rather than a deliberate choice. shrink keeps the outlined
              // label from sitting over that text.
              slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
              onChange={(e) =>
                setData({ ...data, projectId: parseNullableInt(e.target.value) })
              }
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {projects.filter(Boolean).map((project) => {
                const label = projectOptionLabel(project);
                return (
                  <MenuItem key={project.id} value={project.id}>
                    {project.inactive ? `${label} (inactive)` : label}
                  </MenuItem>
                );
              })}
            </TextField>
          </MDBox>
          {isBulkMode && locationField}
        </MDBox>
        {/* Role-gated: a member sees neither child, so the row would render as an empty box.
            Equal halves — the Date / Location row below matches, so the second field of each aligns. */}
        {(selfTargetEnabled ||
          (role !== AppConstants.ROLE_EMPLOYEE_MEMBER &&
            activityTarget === ACTIVITY_TARGET_MEMBER)) && (
        <MDBox
          my={2}
          display="grid"
          gap={2}
          alignItems="flex-start"
          sx={{ gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" } }}
        >
        {selfTargetEnabled && (
          <MDBox>
            <FormControl fullWidth>
              <OutlinedRadioField label="Activity for">
                <RadioGroup
                  row
                  className="add-activity-radio-row"
                  aria-label="Activity for"
                  name="activity-target"
                  value={activityTarget}
                  onChange={(e) => handleActivityTargetChange(e.target.value)}
                >
                  <FormControlLabel
                    value={ACTIVITY_TARGET_SELF}
                    control={<Radio />}
                    label="Myself"
                    disabled={Boolean(editObject) || resolvingSelf || !selfRadioEnabled}
                  />
                  <FormControlLabel
                    value={ACTIVITY_TARGET_MEMBER}
                    control={<Radio />}
                    label="Team member"
                    disabled={Boolean(editObject)}
                  />
                </RadioGroup>
              </OutlinedRadioField>
              {/* Add-only hints: the owner is locked while editing, so they don't apply. */}
              {!editObject && !resolvingSelf && !selfManagerId && (
                <FormHelperText>Manager profile not resolved for your account</FormHelperText>
              )}
              {/* Wait for the subtree to load (non-empty) before judging "manages"; otherwise
                  this flashes while the subtree fetch is still in flight after selfManagerId
                  resolves — the subtree always holds ≥1 of the manager's own records. */}
              {!editObject && !resolvingSelf && selfManagerId && subtreeManagers.length > 0 && !managesSelectedClient && (
                <FormHelperText>You do not directly manage the selected client</FormHelperText>
              )}
              {/* Owner error for a self-activity: the member dropdown is hidden, so it has
                  nowhere else to appear. */}
              {fieldErrors.managerId && (
                <FormHelperText error>{fieldErrors.managerId}</FormHelperText>
              )}
            </FormControl>
          </MDBox>
        )}
        {/* Spans the row for an admin, who gets no "Activity for" column beside it. */}
        {role !== AppConstants.ROLE_EMPLOYEE_MEMBER && activityTarget === ACTIVITY_TARGET_MEMBER && (
          <MDBox sx={{ gridColumn: selfTargetEnabled ? "auto" : "1 / -1" }}>
            {editObject ? (
              // Editing never reassigns the owner, so render the saved name as plain
              // read-only text instead of a locked <TextField select>. A select only
              // paints its display when its value both matches a MenuItem and is
              // non-empty; for an admin editing a manager-owned row the owner id lives
              // in managerId (ownerId is null by design), so the value fell back to ""
              // and the field rendered blank. Text has no value-to-option coupling.
              <TextField
                fullWidth
                disabled
                variant="outlined"
                id="member"
                label="Member / Manager"
                value={editObject.memberName ?? ""}
                sx={{ width: "100%" }}
              />
            ) : (
              <TextField
                select
                required
                fullWidth
                variant="outlined"
                id="member"
                label="Member / Manager"
                value={ownerFieldValue}
                onChange={(e) => {
                  if (role === AppConstants.ROLE_EMPLOYEE_MANAGER) handleOwnerSelect(e.target.value);
                  else setData({ ...data, ownerId: parseInt(e.target.value) });
                  clearFieldError("ownerId");
                }}
                error={Boolean(fieldErrors.ownerId)}
                helperText={fieldErrors.ownerId}
              >
                {/* For the manager role, render Members + (optional) Managers optgroups */}
                {role === AppConstants.ROLE_EMPLOYEE_MANAGER ? (
                (() => {
                  // L1 managers for the selected client (exclude all the L2's own records)
                  const selfIdSet = new Set(selfManagerIds.map(String));
                  const l1ManagersForClient = sortByText(
                    subtreeManagers.filter(
                      (m) =>
                        String(m.clientId) === String(selectedClientId) &&
                        !selfIdSet.has(String(m.id))
                    ),
                    l1ManagerLabel
                  );
                  const memberItems = dialogMembers?.filter(Boolean).map((member) => (
                    <MenuItem key={`mbr-${member.id}`} value={member.id}>
                      {member.name}
                    </MenuItem>
                  ));
                  if (l1ManagersForClient.length === 0) {
                    // No L1s for this client — just show the flat member list (L1 path)
                    return memberItems;
                  }
                  return [
                    <MenuItem key="__grp-members" disabled sx={{ opacity: 0.6, fontStyle: "italic", fontSize: "0.8rem" }}>
                      Members
                    </MenuItem>,
                    ...(memberItems ?? []),
                    <MenuItem key="__grp-managers" disabled sx={{ opacity: 0.6, fontStyle: "italic", fontSize: "0.8rem" }}>
                      Managers (on behalf)
                    </MenuItem>,
                    ...l1ManagersForClient.map((mgr) => (
                      <MenuItem key={`mgr-${mgr.id}`} value={`${MANAGER_OPTION_PREFIX}${mgr.id}`}>
                        {l1ManagerLabel(mgr)}
                      </MenuItem>
                    )),
                  ];
                })()
                ) : (
                  dialogMembers?.filter(Boolean).map((member) => (
                    <MenuItem key={member.id} value={member.id}>
                      {member.name}
                    </MenuItem>
                  ))
                )}
              </TextField>
            )}
          </MDBox>
        )}
        </MDBox>
        )}
        {/* One activity: date, location, hours, type and status are fields of their own. The
            table owns all five instead, one set per row, so the two are exclusive.
            @since NEXT_VERSION */}
        {!isBulkMode && (
        <>
        {/* Wider margin below than above, to set the paired rows apart from Project. */}
        <MDBox
          mt={2}
          mb={3}
          display="grid"
          gap={2}
          alignItems="flex-start"
          sx={{ gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" } }}
        >
          <MDBox className="add-activity-date-input">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                minDate={dayjs(earliestDateAllowed)}
                maxDate={dayjs(getLatestActivityDate())}
                label="Date"
                value={data.startDate ? dayjs(data.startDate) : null}
                onChange={(newStartDate) => {
                  const formatted = newStartDate ? getDateInFormat(newStartDate) : null;
                  // End date is not on the form any more, so it is kept equal to the start date
                  // here. The backend column is NOT NULL, and a one-day activity is the only
                  // shape the form has ever been able to produce.
                  const next = { ...data, startDate: formatted, endDate: formatted };
                  setData(next);
                  const { fieldErrors: errs } = validateActivity(next, {
                    earliestDate: earliestDateAllowed,
                  });
                  setFieldErrors((prev) => ({
                    ...prev,
                    startDate: errs.startDate,
                    endDate: errs.endDate,
                  }));
                }}
                format={AppConstants.DATE_FORMAT_DISPLAY}
                slotProps={{
                  textField: {
                    required: true,
                    error: Boolean(fieldErrors.startDate),
                    helperText:
                      fieldErrors.startDate && toDateFieldMessage(fieldErrors.startDate),
                    // Keeps the label on the border even with no value, so an empty field (a
                    // copied activity) shows the DD-MM-YYYY format rather than the label.
                    InputLabelProps: { shrink: true },
                  },
                }}
              />
            </LocalizationProvider>
          </MDBox>
          {locationField}
        </MDBox>
        <MDBox my={2}>
          {/* Relabelled from "Project Code / Ticket": the project is now a real selection above,
              so this field carries only the task ticket or external reference. The stored column
              is still client_ticket_ref. @since NEXT_VERSION */}
          <TextField
            id="ticket"
            label="Task Ticket / Details"
            fullWidth
            variant="outlined"
            value={data && data.clientTicketRef}
            onBlur={trimOnBlur(setData, "clientTicketRef")}
            onChange={(e) => setData({ ...data, clientTicketRef: e.target.value })}
            slotProps={{
              htmlInput: { maxLength: AppConstants.MAX_CLIENT_TICKET_REF_LENGTH },
            }}
          />
        </MDBox>
        <MDBox my={2}>
          <TextField
            id="description"
            label="Description"
            fullWidth
            variant="outlined"
            value={data && data.description}
            onBlur={trimOnBlur(setData, "description")}
            onChange={(e) => {
              setData({ ...data, description: e.target.value });
              clearFieldError("description");
            }}
            error={Boolean(fieldErrors.description)}
            helperText={fieldErrors.description}
          />
        </MDBox>
        <MDBox my={2}>
          <TextField
            id="hours"
            label="Hours Spent"
            type="number"
            required
            fullWidth
            variant="outlined"
            inputRef={hoursInputRef}
            value={data?.hoursSpent ?? ""}
            // Prevent the mouse wheel from silently incrementing/decrementing the
            // number input while it has focus. Blurring lets the scroll fall
            // through to the dialog instead.
            onWheel={(e) => e.target.blur()}
            onChange={(e) => {
              const raw = e.target.value;
              const parsed = raw === "" ? NaN : parseFloat(raw);
              const next = { ...data, hoursSpent: parsed };
              setData(next);
              const { fieldErrors: errs } = validateActivity(next, {
                earliestDate: earliestDateAllowed,
              });
              setFieldErrors((prev) => ({ ...prev, hoursSpent: errs.hoursSpent }));
            }}
            error={Boolean(fieldErrors.hoursSpent)}
            helperText={fieldErrors.hoursSpent}
            slotProps={{
              htmlInput: {
                min: 0.5,
                max: AppConstants.MAXIMUM_ACTIVITY_HOURS_PER_DAY,
                step: 0.5,
              },
            }}
          />
        </MDBox>
        <MDBox my={2}>
          <TextField
            select
            required
            id="activityType"
            label="Activity Type"
            fullWidth
            variant="outlined"
            value={data && parseInt(data.activityTypeId)}
            onChange={(e) => {
              setData({ ...data, activityTypeId: parseInt(e.target.value) });
              clearFieldError("activityTypeId");
            }}
            error={Boolean(fieldErrors.activityTypeId)}
            helperText={fieldErrors.activityTypeId}
          >
            {activityTypes?.filter(Boolean).map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.activityDesc}
                </MenuItem>
              ))}
          </TextField>
        </MDBox>
        <MDBox my={2}>
          <TextField
            select
            id="addActivityStatus"
            label="Status"
            fullWidth
            variant="outlined"
            value={data && data.status}
            onChange={(e) => {
              setData({ ...data, status: e.target.value });
              clearFieldError("status");
            }}
            error={Boolean(fieldErrors.status)}
            helperText={fieldErrors.status}
          >
            {AppConstants.ACTIVITY_STATUSES.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </MDBox>
        </>
        )}
        {isBulkMode && (
        <MDBox my={2}>
            {/* @since NEXT_VERSION */}
            <Collapse in={Boolean(clientSwitchNotice)}>
              <Alert
                severity="warning"
                sx={{ mb: 2 }}
                action={
                  <IconButton
                    aria-label="close"
                    color="inherit"
                    size="small"
                    onClick={() => setClientSwitchNotice(null)}
                  >
                    <CloseIcon fontSize="inherit" />
                  </IconButton>
                }
              >
                {clientSwitchNotice}
              </Alert>
            </Collapse>
            <EntryRowsTable
              rows={rows}
              rowErrors={rowErrors}
              activityTypes={activityTypes}
              savedHoursByDate={savedHoursByDate}
              savedHoursLoading={savedHoursLoading}
              onVisibleMonthChange={(month) => setVisibleMonth(month.format("YYYY-MM"))}
              minDate={dayjs(earliestDateAllowed)}
              onRowsChange={(next) => {
                setRows(next);
                setRowErrors([]);
                setBatchError(null);
              }}
            />
        </MDBox>
        )}
        <Collapse in={messageOpen}>
          <Alert
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => {
                  setMessageOpen(false);
                }}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
            severity={severity}
            sx={{ mb: 2 }}
          >
            {message}
          </Alert>
        </Collapse>
      </DialogContent>
      <DialogActions>
        {/* @since NEXT_VERSION */}
        {isBulkMode && (
          <MDBox mr="auto" display="flex" alignItems="center" gap={1.5}>
            <MDTypography variant="button" fontWeight="medium" color="dark">
              {`${rows.length} ${rows.length === 1 ? "entry" : "entries"} · ${dayRollUp(rows, {}).reduce(
                (sum, day) => sum + day.batchHours,
                0
              )} h`}
            </MDTypography>
            {batchError && (
              <MDTypography variant="caption" color="error" fontWeight="medium">
                {batchError}
              </MDTypography>
            )}
          </MDBox>
        )}
        <Button onClick={() => setAddActivityOpen(false)}>Cancel</Button>
        <Button
          onClick={isBulkMode ? saveActivitiesInBackend : saveActivityInBackend}
          variant="contained"
          disabled={submitting || (isBulkMode && savedHoursLoading)}
        >
          {editObject ? `Update` : rows.length > 1 ? `Save ${rows.length} entries` : `Save`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default memo(AddActivityDialog);
